import { create } from 'zustand'
import { uid } from '@/lib/id'

export interface Toast {
  id: string
  message: string
  tone: 'info' | 'success' | 'error'
  action?: { label: string; run: () => void }
}

interface PasswordPrompt {
  fileName: string
  incorrect: boolean
  resolve: (pw: string | null) => void
}

interface FeedbackState {
  toasts: Toast[]
  busy: { label: string; progress?: number } | null
  password: PasswordPrompt | null
  toast(message: string, tone?: Toast['tone'], action?: Toast['action']): void
  dismiss(id: string): void
  setBusy(b: FeedbackState['busy']): void
  askPassword(fileName: string, incorrect: boolean): Promise<string | null>
  answerPassword(pw: string | null): void
}

export const useFeedback = create<FeedbackState>()((set, get) => ({
  toasts: [],
  busy: null,
  password: null,
  toast(message, tone = 'info', action) {
    const id = uid('t')
    set((s) => ({ toasts: [...s.toasts.slice(-3), { id, message, tone, action }] }))
    setTimeout(() => get().dismiss(id), tone === 'error' ? 7000 : action ? 6000 : 3200)
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  setBusy: (busy) => set({ busy }),
  askPassword(fileName, incorrect) {
    return new Promise((resolve) => set({ password: { fileName, incorrect, resolve } }))
  },
  answerPassword(pw) {
    get().password?.resolve(pw)
    set({ password: null })
  },
}))

export const toast = (...args: Parameters<FeedbackState['toast']>) => useFeedback.getState().toast(...args)
